// src/components/PublicLayout.js
import React, { useState, useEffect } from 'react';
import { _getFromLocalStorage, _saveToLocalStorage } from '../api/apiService';
import ProductDetailModal from './ProductDetailModal';
import CartModal          from './CartModal';

const bs = { fontFamily: "'Montserrat', sans-serif" };
const ds = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

// FIX: recibe onNavEvento y onNavHome para cambiar página pública desde el navbar
const PublicLayout = ({ children, onNavEvento, onNavHome }) => {
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [selectedProduct,   setSelectedProduct]   = useState(null);
  const [showCart,          setShowCart]          = useState(false);
  const [cartItemCount,     setCartItemCount]     = useState(0);
  const [scrolled,          setScrolled]          = useState(false);
  const [mobileOpen,        setMobileOpen]        = useState(false);

  useEffect(() => {
    const updateCount = () => {
      const cart = _getFromLocalStorage('publicCart') || [];
      setCartItemCount(cart.reduce((n, i) => n + i.quantity, 0));
    };
    updateCount();
    window.addEventListener('storage', updateCount);
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('storage', updateCount);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const scrollTo = id => {
    setMobileOpen(false);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 80);
  };

  // FIX: navegar a página de evento sin scroll
  const handleNavEvento = () => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (onNavEvento) onNavEvento();
  };

  const handleNavHome = () => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (onNavHome) onNavHome();
  };

  const handleOpenProductDetail = (product) => {
    setSelectedProduct(product);
    setShowProductDetail(true);
  };

  const handleCloseProductDetail = () => {
    setSelectedProduct(null);
    setShowProductDetail(false);
  };

  const handleAddToCart = (product, variant, quantity) => {
    const cart = _getFromLocalStorage('publicCart') || [];
    const idx  = cart.findIndex(i =>
      i.productId === product.id && i.variantId === `${variant.size}-${variant.color}`
    );
    if (idx > -1) {
      cart[idx].quantity += quantity;
    } else {
      cart.push({
        productId:   product.id,
        productName: product.name,
        variantId:   `${variant.size}-${variant.color}`,
        variantInfo: `Talla: ${variant.size}${variant.color ? ` — Color: ${variant.color}` : ''}`,
        price:       product.price,
        quantity,
        image:       product.images?.[0] ?? null,
      });
    }
    _saveToLocalStorage('publicCart', cart);
    setCartItemCount(cart.reduce((n, i) => n + i.quantity, 0));
    window.dispatchEvent(new Event('storage'));
    setShowCart(true);
  };

  // FIX: NAV ahora incluye Evento con handler especial
  const NAV = [
    { label: 'Tienda',   action: () => scrollTo('store-section') },
    { label: 'Academia', action: () => scrollTo('academia-section') },
    { label: 'Evento',   action: handleNavEvento, highlight: true }, // FIX: nuevo
    { label: 'Contacto', action: () => scrollTo('contact-section') },
  ];

  const navColor  = scrolled ? '#060D14' : 'white';
  const logoColor = scrolled ? '#961914' : 'white';

  return (
    <div style={{ minHeight: '100vh', background: 'white', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300&family=Montserrat:wght@300;400;500;600&display=swap');
        :root {
          --font-display: 'Cormorant Garamond', Georgia, serif;
          --font-body: 'Montserrat', sans-serif;
        }
        body { font-family: var(--font-body); }
        .elede-nav-desktop { display: none; }
        .elede-hamburger   { display: flex; }
        @media (min-width: 768px) {
          .elede-nav-desktop { display: flex; align-items: center; gap: 32px; }
          .elede-hamburger   { display: none; }
        }
      `}</style>

      {/* Anuncio superior */}
      <div style={{ background: '#060D14', color: 'rgba(255,255,255,0.55)',
        fontSize: '10px', letterSpacing: '0.18em', textAlign: 'center',
        padding: '7px 12px', textTransform: 'uppercase', ...bs,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        Boutique de Danza · Tlalpan Centro, CDMX · Envíos a todo México
      </div>

      {/* Header sticky */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: scrolled ? 'rgba(255,255,255,0.97)' : '#961914',
        borderBottom: scrolled ? '1px solid #f0e8e8' : 'none',
        boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.06)' : 'none',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        transition: 'all 0.3s ease',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: '60px' }}>

          {/* Logo — click regresa al home */}
          <button onClick={handleNavHome}
            style={{ display: 'flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
            <img src="/logo-elede.png" alt="ELEDÉ"
              style={{
                height: '36px', width: '36px', objectFit: 'cover',
                borderRadius: '50%',
                border: scrolled ? '2px solid #e5c9c9' : '2px solid rgba(255,255,255,0.3)',
                transition: 'border-color 0.3s',
              }} />
            <span style={{ ...ds, fontSize: '18px', fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: logoColor, transition: 'color 0.3s' }}>
              ELEDÉ
            </span>
          </button>

          {/* Nav desktop */}
          <nav className="elede-nav-desktop">
            {NAV.map(({ label, action, highlight }) => (
              <button key={label} onClick={action} style={{
                background: highlight
                  ? (scrolled ? '#961914' : 'rgba(255,255,255,0.2)')
                  : 'none',
                border: highlight ? 'none' : 'none',
                cursor: 'pointer',
                fontSize: '11px', fontWeight: 600, letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: highlight
                  ? (scrolled ? 'white' : 'white')
                  : navColor,
                transition: 'opacity 0.2s, background 0.2s',
                padding: highlight ? '6px 14px' : '4px 0',
                borderRadius: highlight ? '2px' : '0',
                ...bs,
              }}
              onMouseOver={e => e.currentTarget.style.opacity = '0.75'}
              onMouseOut={e => e.currentTarget.style.opacity = '1'}>
                {highlight ? '🎭 ' : ''}{label}
              </button>
            ))}
          </nav>

          {/* Carrito + hamburguesa */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            <button onClick={() => setShowCart(true)}
              style={{ position: 'relative', background: 'none', border: 'none',
                cursor: 'pointer', color: navColor, padding: '2px',
                display: 'flex', alignItems: 'center' }}>
              <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6}
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
              </svg>
              {cartItemCount > 0 && (
                <span style={{
                  position: 'absolute', top: '-5px', right: '-7px',
                  background: scrolled ? '#961914' : 'white',
                  color: scrolled ? 'white' : '#961914',
                  borderRadius: '50%', width: '17px', height: '17px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '9px', fontWeight: 700,
                }}>{cartItemCount}</span>
              )}
            </button>

            <button className="elede-hamburger"
              onClick={() => setMobileOpen(v => !v)}
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                color: navColor, alignItems: 'center', justifyContent: 'center' }}>
              <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M6 18L18 6M6 6l12 12"/>
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16M4 12h16M4 18h16"/>}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div style={{ background: '#060D14', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            {NAV.map(({ label, action, highlight }) => (
              <button key={label} onClick={action} style={{
                display: 'block', width: '100%', textAlign: 'left',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.05)',
                fontSize: '11px', fontWeight: 600, letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: highlight ? '#f0a0a0' : 'rgba(255,255,255,0.8)',
                ...bs,
              }}>
                {highlight ? '🎭 ' : ''}{label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Main */}
      <main style={{ flex: 1 }}>
        {React.Children.map(children, child =>
          React.isValidElement(child)
            ? React.cloneElement(child, { onProductClick: handleOpenProductDetail })
            : child
        )}
      </main>

      {/* Footer */}
      <footer id="contact-section">
        <div style={{ background: '#961914', padding: '48px 24px' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px',
              letterSpacing: '0.3em', textTransform: 'uppercase',
              marginBottom: '36px', ...bs }}>
              Contáctanos
            </p>
            <div style={{ display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: '32px' }}>
              {[
                { label: 'WhatsApp',  val: '55 6938 0380',        href: 'https://wa.me/525569380380' },
                { label: 'Email',     val: 'elededanza@gmail.com', href: 'mailto:elededanza@gmail.com' },
                { label: 'Instagram', val: '@elededanza',          href: 'https://instagram.com/elededanza' },
              ].map(({ label, val, href }) => (
                <div key={label}>
                  <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px',
                    letterSpacing: '0.25em', textTransform: 'uppercase',
                    marginBottom: '8px', ...bs }}>{label}</p>
                  <a href={href} target="_blank" rel="noopener noreferrer" style={{
                    color: 'white', fontWeight: 600, fontSize: '14px',
                    textDecoration: 'none', ...bs, wordBreak: 'break-word',
                  }}>{val}</a>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={{ background: '#060D14', padding: '24px', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '10px', marginBottom: '8px' }}>
            <img src="/logo-elede.png" alt="ELEDÉ"
              style={{ height: '34px', width: '34px', objectFit: 'cover',
                borderRadius: '50%', opacity: 0.6 }} />
            <span style={{ ...ds, color: 'rgba(255,255,255,0.4)', fontWeight: 600,
              letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '12px' }}>
              ELEDÉ
            </span>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '10px',
            letterSpacing: '0.06em', ...bs }}>
            © {new Date().getFullYear()} ELEDÉ Danza — Todos los derechos reservados
          </p>
        </div>
      </footer>

      {showProductDetail && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={handleCloseProductDetail}
          onAddToCart={handleAddToCart}
        />
      )}
      {showCart && (
        <CartModal onClose={() => setShowCart(false)} />
      )}
    </div>
  );
};

export default PublicLayout;
